import React, { useEffect } from 'react';

const AccessibilityManager: React.FC = () => {
  useEffect(() => {
    // Function to check if an element is interactive (should be accessible when focused)
    const isInteractiveElement = (element: Element): boolean => {
      const tagName = element.tagName.toLowerCase();
      const role = element.getAttribute('role');
      
      // Interactive HTML elements
      if (['button', 'input', 'select', 'textarea', 'a'].includes(tagName)) {
        return true;
      }
      
      // Elements with interactive roles
      if (role && ['button', 'link', 'menuitem', 'tab', 'checkbox', 'radio', 'textbox', 'combobox', 'listbox', 'option'].includes(role)) {
        return true;
      }
      
      // Check if element is focusable
      if (element.hasAttribute('tabindex') && element.getAttribute('tabindex') !== '-1') {
        return true;
      }
      
      return false;
    };

    // Function to check if we should avoid interfering with this element
    const shouldAvoidInterference = (element: Element): boolean => {
      const tagName = element.tagName.toLowerCase();
      const type = element.getAttribute('type');
      const role = element.getAttribute('role');
      
      // Avoid interfering with search inputs and form elements that might cause loops
      if (tagName === 'input' && (type === 'search' || type === 'text')) {
        return true;
      }
      
      // Avoid interfering with Material-UI components that manage their own accessibility
      if (element.closest('[data-mui-component]') || 
          element.closest('.MuiMenu-root') || 
          element.closest('.MuiPopover-root') ||
          element.closest('[id$="-menu"]')) {
        return true;
      }
      
      // Avoid interfering with navigation dropdowns specifically
      if (element.closest('#access-mgmt-menu') || 
          element.closest('#admin-menu') || 
          element.closest('#codes-menu')) {
        return true;
      }
      
      return false;
    };

    // Function to check and fix aria-hidden on any element that contains focus
    const checkAndFixAriaHidden = (target: HTMLElement) => {
      if (target.getAttribute('aria-hidden') === 'true') {
        const focusedElement = document.activeElement;
        if (focusedElement && target.contains(focusedElement)) {
          // Only remove aria-hidden if the focused element is interactive and we should interfere
          if (isInteractiveElement(focusedElement) && !shouldAvoidInterference(focusedElement)) {
            // Add a small delay to prevent conflicts with Material-UI menu behavior
            setTimeout(() => {
              if (target.getAttribute('aria-hidden') === 'true') {
                target.removeAttribute('aria-hidden');
                console.log('Removed aria-hidden from element containing interactive focus:', target);
              }
            }, 100); // Increased delay to prevent conflicts
          }
        }
      }
    };

    // Create a MutationObserver to watch for aria-hidden changes on ALL elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const target = mutation.target as HTMLElement;
          checkAndFixAriaHidden(target);
        }
        // Also check for new nodes being added that might have aria-hidden
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              // Check the added element itself
              checkAndFixAriaHidden(element);
              // Check all descendants with aria-hidden
              const ariaHiddenElements = element.querySelectorAll('[aria-hidden="true"]');
              ariaHiddenElements.forEach((el) => checkAndFixAriaHidden(el as HTMLElement));
            }
          });
        }
      });
    });

    // Start observing the entire document for attribute changes and child list changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true
    });

    // Also set up a focus event listener to immediately fix any aria-hidden issues
    const handleFocusChange = () => {
      const focusedElement = document.activeElement;
      if (focusedElement && isInteractiveElement(focusedElement) && !shouldAvoidInterference(focusedElement)) {
        // Find any ancestor with aria-hidden="true"
        let parent = focusedElement.parentElement;
        while (parent) {
          if (parent.getAttribute('aria-hidden') === 'true') {
            // Add a small delay to prevent conflicts with Material-UI menu behavior
            setTimeout(() => {
              if (parent && parent.getAttribute('aria-hidden') === 'true') {
                parent.removeAttribute('aria-hidden');
                console.log('Removed aria-hidden from focused element ancestor:', parent);
              }
            }, 100); // Increased delay to prevent conflicts
          }
          parent = parent.parentElement;
        }
      }
    };

    // Listen for focus events
    document.addEventListener('focusin', handleFocusChange, true);
    document.addEventListener('focus', handleFocusChange, true);

    // Initial check for any existing aria-hidden elements
    const existingAriaHiddenElements = document.querySelectorAll('[aria-hidden="true"]');
    existingAriaHiddenElements.forEach((el) => checkAndFixAriaHidden(el as HTMLElement));

    // Cleanup function
    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', handleFocusChange, true);
      document.removeEventListener('focus', handleFocusChange, true);
    };
  }, []);

  return null; // This component doesn't render anything
};

export default AccessibilityManager; 